import { JsonLd } from "../components/JsonLd";
import { Seo } from "../components/Seo";
import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Search, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHero } from "../components/PageHero";
import { getCategory } from "../data/products";
import { VG_ORANGE } from "../lib/brand";
import { renderProductName } from "../lib/productName";

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const category = getCategory(categorySlug);
  const [q, setQ] = useState("");

  if (!category) return <Navigate to="/products" replace />;

  const filtered = category.products.filter((p) =>
    p.name.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="category-page">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${typeof window !== "undefined" ? window.location.origin : ""}/` },
            { "@type": "ListItem", position: 2, name: "Products", item: `${typeof window !== "undefined" ? window.location.origin : ""}/products` },
            { "@type": "ListItem", position: 3, name: category.name, item: `${typeof window !== "undefined" ? window.location.origin : ""}/products/${category.slug}` },
          ],
        }}
      />
      <Seo
        title={category.name}
        description={category.blurb || `Explore ${category.name} from van Gelder, Inc. — commercial flooring and matting engineered for performance in every environment.`}
        image={category.image}
      />
      <PageHero title={category.name} subtitle={category.blurb} image={category.image} testId="category-hero" />

      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-neutral-500 mb-6" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#ED8B00]">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/products" className="hover:text-[#ED8B00]">Products</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-neutral-900 font-medium">{category.name}</span>
        </nav>

        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <p className="text-neutral-600">{filtered.length} product{filtered.length === 1 ? "" : "s"}</p>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              data-testid="category-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search this category..."
              className="pl-9"
            />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 pb-14">
          {filtered.map((p) => (
            <Link
              key={p.slug}
              to={`/products/${category.slug}/${p.slug}`}
              data-testid={`product-card-${p.slug}`}
              className="group flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
            >
              <div className="h-40 overflow-hidden" style={{ backgroundColor: (p.thumbFit === "contain" || category.slug === "accessories") ? "#ffffff" : "#f5f5f5" }}>
                {(p.thumbFit === "contain" || category.slug === "accessories") ? (
                  <img src={p.image} alt={p.name} className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                )}
              </div>
              <div className="flex flex-1 flex-col p-4">
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: VG_ORANGE }}>
                  {category.name}
                </span>
                <h3 className="mt-1 font-heading text-base font-bold text-neutral-900 group-hover:text-[#ED8B00]">{renderProductName(p.name)}</h3>
                <span className="mt-3 inline-flex items-center text-sm font-semibold text-[#ED8B00]">
                  View details <ArrowRight className="ml-1 h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
          {filtered.length === 0 && (
            <p className="col-span-full text-neutral-500 py-10 text-center">No products match your search.</p>
          )}
        </div>
      </div>
    </div>
  );
}
